/**
 * Shared types + helpers for workflow limits display (sl-tdgy.5/.6/.8).
 *
 * DRY: Used by LimitsSection (sidebar) and WorkspaceSettingAutomations (settings).
 */

import { workspaceApiFetch } from '../../cloud';

// =============================================================================
// Types
// =============================================================================

export interface WorkflowLimits {
  maxStepsPerWorkflow: number;
  maxStepDurationMs: number;
  maxWorkflowDurationMs: number;
  maxConcurrentWorkflows: number;
  concurrentCurrent: number;
}

// =============================================================================
// Helpers
// =============================================================================

export function formatDuration(ms: number): string {
  if (ms >= 60_000) {
    const min = Math.round(ms / 60_000);
    return `${min} min`;
  }
  return `${Math.round(ms / 1000)}s`;
}

// =============================================================================
// GraphQL
// =============================================================================

const WORKFLOW_LIMITS_QUERY = `query WorkflowLimits($id: String!) {
  workspace(id: $id) {
    workflowLimits {
      maxStepsPerWorkflow
      maxStepDurationMs
      maxWorkflowDurationMs
      maxConcurrentWorkflows
      concurrentCurrent
    }
  }
}`;

/**
 * Fetch workflow limits for a workspace. Returns null on error.
 */
export async function fetchWorkflowLimits(
  workspaceId: string
): Promise<WorkflowLimits | null> {
  try {
    const res = await workspaceApiFetch(
      '/graphql',
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: WORKFLOW_LIMITS_QUERY,
          variables: { id: workspaceId },
        }),
      },
      {
        workspaceId,
        category: 'workspace-api',
        key: 'workflow-limits',
        mode: 'background',
        priority: 'background',
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.data?.workspace?.workflowLimits ?? null;
  } catch {
    return null;
  }
}
